import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
    GetKokpitEnvironmentNamesRequest,
    GetKokpitEnvironmentRequest,
    GetKokpitEnvironmentsRequest,
    KokpitEnvironmentDto,
    KokpitEnvironmentListDto,
    KokpitEnvironmentUpdateDto,
    NameDTO,
    UpdateCustomersFromKokpitRequest,
    useKokpitEnvironmentApi,
} from "api"
import { StoreUseQueryOptions } from "types"

export const kokpitEnvironmentKeys = {
    all: ["kokpitEnvironment"] as const,
    lists: () => [...kokpitEnvironmentKeys.all, "list"] as const,
    list: (param: GetKokpitEnvironmentsRequest) => [...kokpitEnvironmentKeys.lists(), param] as const,
    details: () => [...kokpitEnvironmentKeys.all, "detail"] as const,
    detail: (param: GetKokpitEnvironmentRequest) => [...kokpitEnvironmentKeys.details(), param] as const,

    nameList: (param: GetKokpitEnvironmentNamesRequest) => [...kokpitEnvironmentKeys.lists(), "name", param] as const,
}

export const useKokpitEnvironment = (
    id: number,
    options: StoreUseQueryOptions<KokpitEnvironmentListDto> = {
        notifyOnChangeProps: ["data", "isFetching", "isFetching"],
    }
) => {
    const api = useKokpitEnvironmentApi()

    const param: GetKokpitEnvironmentRequest = { id }
    return useQuery({
        queryKey: kokpitEnvironmentKeys.detail(param),
        queryFn: () => api.getKokpitEnvironment(param),
        ...options,
    })
}
export const useKokpitEnvironmentList = (
    param: GetKokpitEnvironmentsRequest,
    options?: StoreUseQueryOptions<KokpitEnvironmentListDto[]>
) => {
    const api = useKokpitEnvironmentApi()

    return useQuery({
        queryKey: kokpitEnvironmentKeys.list(param),
        queryFn: () => api.getKokpitEnvironments(param),
        ...options,
    })
}

export const useAddKokpitEnvironmentMutation = () => {
    const api = useKokpitEnvironmentApi()
    const queryClient = useQueryClient()

    return useMutation<KokpitEnvironmentListDto, any, KokpitEnvironmentDto, any>({
        mutationFn: (d) => api.postKokpitEnvironment({ kokpitEnvironmentDto: d }),

        onSettled: (_data, error, _variables) => {
            if (!error || error.status !== 400) {
                queryClient.invalidateQueries({ queryKey: kokpitEnvironmentKeys.lists() })
            }
        },
    })
}

export const useUpdateKokpitEnvironmentMutation = () => {
    const api = useKokpitEnvironmentApi()
    const queryClient = useQueryClient()

    return useMutation<void, any, KokpitEnvironmentUpdateDto, any>({
        mutationFn: (d) => api.putKokpitEnvironment({ id: d.id!, kokpitEnvironmentUpdateDto: d }),

        onSettled: (_data, error, variables) => {
            if (!error || error.status !== 400) {
                queryClient.invalidateQueries({ queryKey: kokpitEnvironmentKeys.detail({ id: variables.id! }) })
                queryClient.invalidateQueries({ queryKey: kokpitEnvironmentKeys.lists() })
            }
        },
    })
}

export const useKokpitEnvironmentNameList = (
    param: GetKokpitEnvironmentNamesRequest,
    options: StoreUseQueryOptions<NameDTO[]> = {
        placeholderData: [],
        notifyOnChangeProps: ["data", "isFetching", "isFetching"],
    }
) => {
    const api = useKokpitEnvironmentApi()

    return useQuery({
        queryKey: kokpitEnvironmentKeys.nameList(param),
        queryFn: () => api.getKokpitEnvironmentNames(param),
        ...options,
    })
}

export const useImportCustomerMutation = () => {
    const api = useKokpitEnvironmentApi()
    const queryClient = useQueryClient()

    return useMutation<NameDTO[], any, UpdateCustomersFromKokpitRequest, any>({
        mutationFn: (d) => api.updateCustomersFromKokpit(d),

        onSettled: (_data, error, _variables) => {
            if (!error || error.status !== 400) {
                queryClient.invalidateQueries({ queryKey: kokpitEnvironmentKeys.lists() })
            }
        },
    })
}
